import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconPrinter,
  IconX,
} from '@tabler/icons-react';
import {
  formatExpenseDate,
  getStatusMeta,
} from '../utils/reembolsosStatus';
import { formatPaymentType } from '../utils/reembolsosFormat';
import {
  buildReembolsoReportFilename,
  exportReembolsoReportPdf,
  printReembolsoReport,
} from '../utils/reembolsosDetailReportExport';
import './GestaoReembolsosDetailReportModal.css';

const ROWS_PER_PAGE = 14;
/** Linha com comprovante ocupa o espaço de ~12 linhas (imagem ~76mm) → 1 comprovante por folha. */
const RECEIPT_ROW_WEIGHT = 12;
/** px a 96dpi para A4 paisagem */
const SHEET_W_PX = (297 * 96) / 25.4;
const SHEET_H_PX = (210 * 96) / 25.4;

function rowHasReceipt(row) {
  return Boolean(row?.hasReceipt && row?.receiptUrl);
}

function chunkRows(rows, size, withReceipts) {
  if (!rows.length) return [[]];
  const out = [];
  let page = [];
  let used = 0;
  rows.forEach((row) => {
    const weight = withReceipts && rowHasReceipt(row) ? RECEIPT_ROW_WEIGHT : 1;
    if (page.length && used + weight > size) {
      out.push(page);
      page = [];
      used = 0;
    }
    page.push(row);
    used += weight;
  });
  if (page.length) out.push(page);
  return out;
}

function ReportReceipt({ url, code }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  return (
    <div className="gr-report-receipt">
      {failed ? (
        <span className="gr-report-receipt-fallback">
          Comprovante em formato não visualizável no relatório (ex.: PDF).
        </span>
      ) : (
        <img
          src={url}
          alt={code ? `Comprovante ${code}` : 'Comprovante'}
          className="gr-report-receipt-img"
          crossOrigin="anonymous"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

function formatGeneratedAt(date) {
  try {
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
}

function ReportAvatar({ initials, color, photoUrl, size = 44 }) {
  const [imgErr, setImgErr] = useState(false);
  const showImg = Boolean(photoUrl) && !imgErr;
  return (
    <span
      className="gr-report-avatar"
      style={{
        width: size,
        height: size,
        background: showImg ? 'transparent' : color || '#7c4dff',
      }}
      aria-hidden
    >
      {showImg ? (
        <img
          src={photoUrl}
          alt=""
          className="gr-report-avatar-img"
          crossOrigin="anonymous"
          onError={() => setImgErr(true)}
        />
      ) : (
        <span className="gr-report-avatar-initials">{initials || '?'}</span>
      )}
    </span>
  );
}

/**
 * Preview do relatório A4 paisagem + Imprimir / Gerar PDF (mesmo layout).
 */
export default function GestaoReembolsosDetailReportModal({
  open,
  onClose,
  tech,
  rows = [],
  totalLabel = '0,00',
  periodLabel = '',
}) {
  const sheetsRef = useRef(null);
  const stageRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(() => new Date());
  const [pageIndex, setPageIndex] = useState(0);
  const [fitScale, setFitScale] = useState(1);
  const [showReceipts, setShowReceipts] = useState(false);

  const pages = useMemo(
    () => chunkRows(rows, ROWS_PER_PAGE, showReceipts),
    [rows, showReceipts]
  );
  const pageCount = pages.length;
  const pathLabel = [tech?.coordinatorName, tech?.areaName].filter(Boolean).join(' · ') || '—';
  const generatedLabel = formatGeneratedAt(generatedAt);

  useEffect(() => {
    if (open) {
      setGeneratedAt(new Date());
      setExportError(null);
      setExporting(false);
      setPageIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setPageIndex((i) => Math.min(i, Math.max(0, pageCount - 1)));
  }, [pageCount]);

  useEffect(() => {
    if (!open) return undefined;
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const rect = stage.getBoundingClientRect();
      /* margem horizontal mínima (folha no “fundo”) */
      const padX = 10;
      const padY = 8;
      const sx = (rect.width - padX) / SHEET_W_PX;
      const sy = (rect.height - padY) / SHEET_H_PX;
      const next = Math.max(0.2, Math.min(1, sx, sy));
      setFitScale(Number.isFinite(next) ? next : 1);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !exporting) onClose?.();
      if (pageCount <= 1) return;
      if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        setPageIndex((i) => Math.max(0, i - 1));
      }
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        setPageIndex((i) => Math.min(pageCount - 1, i + 1));
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, exporting, pageCount]);

  const handlePrint = useCallback(() => {
    setExportError(null);
    printReembolsoReport();
  }, []);

  const handlePdf = useCallback(async () => {
    setExportError(null);
    setExporting(true);
    try {
      await exportReembolsoReportPdf(
        sheetsRef.current,
        buildReembolsoReportFilename(tech?.name)
      );
    } catch (err) {
      setExportError(err?.message || 'Não foi possível gerar o PDF.');
    } finally {
      setExporting(false);
    }
  }, [tech?.name]);

  if (!open) return null;

  const modal = (
    <div className="gr-report-modal" role="dialog" aria-modal="true" aria-label="Relatório de reembolsos">
      <div className="gr-report-modal-backdrop" onClick={() => !exporting && onClose?.()} />
      <div className="gr-report-modal-panel">
        <header className="gr-report-modal-toolbar">
          <div className="gr-report-modal-toolbar-titles">
            <span className="gr-report-modal-kicker">Gestão de reembolsos</span>
            <h2 className="gr-report-modal-title">Pré-visualização do relatório</h2>
          </div>
          <div className="gr-report-modal-actions">
            {exportError ? (
              <span className="gr-report-modal-error" role="alert">
                {exportError}
              </span>
            ) : null}
            <label className={`gr-report-switch${exporting ? ' is-disabled' : ''}`}>
              <input
                type="checkbox"
                role="switch"
                className="gr-report-switch-input"
                checked={showReceipts}
                disabled={exporting}
                onChange={(e) => {
                  setShowReceipts(e.target.checked);
                  setPageIndex(0);
                }}
              />
              <span className="gr-report-switch-track" aria-hidden>
                <span className="gr-report-switch-thumb" />
              </span>
              <span className="gr-report-switch-label">Exibir comprovantes</span>
            </label>
            <button
              type="button"
              className="gr-report-action-btn"
              onClick={handlePrint}
              disabled={exporting}
            >
              <IconPrinter size={16} stroke={1.75} aria-hidden />
              Imprimir
            </button>
            <button
              type="button"
              className="gr-report-action-btn is-primary"
              onClick={handlePdf}
              disabled={exporting}
            >
              <IconDownload size={16} stroke={1.75} aria-hidden />
              {exporting ? 'Gerando…' : 'Gerar PDF'}
            </button>
            <button
              type="button"
              className="gr-report-action-btn is-ghost"
              onClick={onClose}
              disabled={exporting}
              aria-label="Fechar"
            >
              <IconX size={16} stroke={1.75} />
            </button>
          </div>
        </header>

        <div className="gr-report-preview-stage" ref={stageRef}>
          <div
            className="gr-report-sheets"
            ref={sheetsRef}
            style={{ '--gr-report-fit-scale': fitScale }}
          >
            {pages.map((pageRows, pageIdx) => {
              const pageNum = pageIdx + 1;
              const isActive = pageIdx === pageIndex;
              return (
                <article
                  key={`page-${pageNum}`}
                  className={`gr-report-sheet${isActive ? ' is-active' : ''}`}
                  aria-label={`Página ${pageNum} de ${pageCount}`}
                  aria-hidden={!isActive}
                >
                  <div className="gr-report-sheet-inner">
                    <header className="gr-report-sheet-head">
                      <div className="gr-report-brand">
                        <span className="gr-report-brand-mark" aria-hidden />
                        <div>
                          <div className="gr-report-brand-title">Relatório de reembolsos</div>
                          <div className="gr-report-brand-sub">
                            Lançamentos do técnico · A4 paisagem
                          </div>
                        </div>
                      </div>
                      <div className="gr-report-head-total">
                        <span className="gr-report-head-total-label">Total</span>
                        <span className="gr-report-head-total-value">R$ {totalLabel}</span>
                      </div>
                    </header>

                    <section className="gr-report-identity">
                      <ReportAvatar
                        initials={tech?.initials}
                        color={tech?.avatarColor}
                        photoUrl={tech?.profilePhotoUrl}
                      />
                      <div className="gr-report-identity-text">
                        <div className="gr-report-identity-name">{tech?.name || 'Técnico'}</div>
                        <div className="gr-report-identity-path">{pathLabel}</div>
                        {periodLabel ? (
                          <div className="gr-report-identity-period">Período {periodLabel}</div>
                        ) : null}
                      </div>
                      <div className="gr-report-identity-meta">
                        <span>
                          {rows.length} {rows.length === 1 ? 'lançamento' : 'lançamentos'}
                        </span>
                        <span>
                          Página {pageNum} / {pageCount}
                        </span>
                      </div>
                    </section>

                    <div className="gr-report-table-wrap">
                      <table className="gr-report-table">
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Código</th>
                            <th>Observação</th>
                            <th>Categoria</th>
                            <th className="is-num">Valor</th>
                            <th>Pagamento</th>
                            <th>Anexo</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.length ? (
                            pageRows.map((row, idx) => {
                              const st = getStatusMeta(row.statusKey, { light: true });
                              const rowKey = String(row.id ?? `${row.code}-${pageIdx}-${idx}`);
                              const withReceipt = showReceipts && rowHasReceipt(row);
                              return (
                                <React.Fragment key={rowKey}>
                                <tr className={withReceipt ? 'has-receipt' : undefined}>
                                  <td className="is-muted">{formatExpenseDate(row.expenseDate)}</td>
                                  <td className="is-mono">{row.code || '—'}</td>
                                  <td className="is-obs">{row.description || '—'}</td>
                                  <td>{row.expenseTypeName || '—'}</td>
                                  <td className="is-num is-strong">{row.amountLabel || '—'}</td>
                                  <td className="is-muted">
                                    {formatPaymentType(row.paymentType) || '—'}
                                  </td>
                                  <td className="is-muted">
                                    {row.hasReceipt && row.receiptUrl ? 'Sim' : '—'}
                                  </td>
                                  <td>
                                    <span className="gr-report-status" style={{ color: st.fg }}>
                                      <span
                                        className="gr-report-status-dot"
                                        style={{ background: st.dot }}
                                      />
                                      {st.label}
                                    </span>
                                  </td>
                                </tr>
                                {withReceipt ? (
                                  <tr className="gr-report-receipt-row">
                                    <td colSpan={8}>
                                      <ReportReceipt url={row.receiptUrl} code={row.code} />
                                    </td>
                                  </tr>
                                ) : null}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={8} className="is-empty">
                                Nenhum lançamento para o filtro atual.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <footer className="gr-report-sheet-foot">
                      <span>Gerado em {generatedLabel}</span>
                      <span>
                        Página {pageNum} de {pageCount}
                      </span>
                    </footer>
                  </div>
                </article>
              );
            })}
          </div>

          {pageCount > 1 ? (
            <div className="gr-report-page-nav" aria-label="Paginação do relatório">
              <button
                type="button"
                className="gr-report-page-nav-btn"
                aria-label="Página anterior"
                disabled={pageIndex <= 0}
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              >
                <IconChevronUp size={16} stroke={2} />
              </button>
              <span className="gr-report-page-nav-label">
                {pageIndex + 1}/{pageCount}
              </span>
              <button
                type="button"
                className="gr-report-page-nav-btn"
                aria-label="Próxima página"
                disabled={pageIndex >= pageCount - 1}
                onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
              >
                <IconChevronDown size={16} stroke={2} />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
